import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db, firebaseConfigured } from '../lib/firebase';

const AuthContext = createContext(null);

const authErrorMessage = (error, fallback = 'Authentication failed') => {
  if (error?.code === 'auth/unauthorized-domain') {
    const host = window.location.hostname;
    return `Google sign-in is blocked because ${host} is not in Firebase Authentication authorized domains. Add ${host} in Firebase Console > Authentication > Settings > Authorized domains, then try again.`;
  }
  if (error?.code === 'auth/popup-closed-by-user') {
    return 'Google sign-in was closed before it finished.';
  }
  return error?.message || fallback;
};

const defaultAdminEmails = ['admin@gmail.com'];
const adminEmails = ['admin@gmail.com'];

const isEmailAdmin = (email) => {
  if (!email) return false;
  const clean = email.toLowerCase().trim();
  // Strictly only admin@gmail.com is granted admin permissions
  return clean === 'admin@gmail.com';
};

const ensureFirebase = () => {
  if (!firebaseConfigured || !auth || !db) {
    throw new Error('Firebase is not configured. Add Firebase values to frontend/.env and rebuild.');
  }
};

const basicUser = (firebaseUser, profile = {}) => {
  const isActualAdmin = isEmailAdmin(firebaseUser.email);
  const rawRole = (profile.role || '').toLowerCase().trim();
  const reqRole = (profile.verification_requested_role || '').toLowerCase().trim();
  const isApproved = isActualAdmin || rawRole === 'general'
    ? true
    : Boolean(profile.is_approved === true || profile.verification_status === 'approved');

  let userRole = 'general';
  if (isActualAdmin) {
    userRole = 'admin';
  } else if (rawRole === 'doctor' || (isApproved && reqRole === 'doctor') || (isApproved && profile.license_number)) {
    userRole = 'doctor';
  } else if (rawRole === 'student' || (isApproved && reqRole === 'student') || (isApproved && (profile.student_id_front || profile.college_name || profile.education_level))) {
    userRole = 'student';
  } else if (rawRole === 'patient') {
    userRole = 'patient';
  } else {
    userRole = rawRole || 'general';
  }

  const verificationStatus = isActualAdmin || userRole === 'general'
    ? 'approved'
    : (profile.verification_status || (isApproved ? 'approved' : 'pending'));

  return {
    id: firebaseUser.uid,
    email: firebaseUser.email,
    full_name:
      profile.full_name ||
      firebaseUser.displayName ||
      firebaseUser.email?.split('@')[0] ||
      (userRole === 'doctor' ? 'Doctor' : userRole === 'student' ? 'Student' : 'Member'),
    role: userRole,
    country: profile.country || '',
    education_level: profile.education_level || '',
    college_name: profile.college_name || profile.campus_name || '',
    campus_name: profile.campus_name || profile.college_name || '',
    goal: profile.goal || '',
    is_admin: isActualAdmin,
    is_approved: isApproved,
    verification_status: verificationStatus,
    verification_requested_role: profile.verification_requested_role || (userRole === 'doctor' ? 'doctor' : (userRole === 'student' ? 'student' : '')),
    verification_submitted_at: profile.verification_submitted_at || profile.created_at || new Date().toISOString(),
    verification_notes: profile.verification_notes || '',
    student_id_front: profile.student_id_front || '',
    student_id_back: profile.student_id_back || '',
    specialization: profile.specialization || '',
    qualifications: profile.qualifications || '',
    license_number: profile.license_number || '',
    hospital_affiliation: profile.hospital_affiliation || '',
    consultation_fee: profile.consultation_fee || '₹1,000',
    experience_years: profile.experience_years || '',
    phone: profile.phone || '',
    city: profile.city || '',
    bio: profile.bio || '',
    image: profile.image || firebaseUser.photoURL || '',
    patient_profiles: Array.isArray(profile.patient_profiles) ? profile.patient_profiles : [],
  };
};

const loadProfile = async (firebaseUser) => {
  if (!firebaseUser) return null;
  const isActualAdmin = isEmailAdmin(firebaseUser.email);
  let profileData = {};

  if (db) {
    try {
      const getDocPromise = getDoc(doc(db, 'users', firebaseUser.uid));
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore timeout')), 2500)
      );
      const profileSnap = await Promise.race([getDocPromise, timeoutPromise]);
      if (profileSnap?.exists && profileSnap.exists()) {
        profileData = profileSnap.data() || {};
      }

      if (isActualAdmin) {
        getDocs(collection(db, 'admins')).then((snap) => {
          snap.docs.forEach((d) => {
            const dEmail = d.data()?.email?.toLowerCase().trim();
            if (dEmail && dEmail !== 'admin@gmail.com') {
              deleteDoc(doc(db, 'admins', d.id)).catch(() => {});
            }
          });
        }).catch(() => {});
      } else {
        if (profileData && (profileData.role === 'admin' || profileData.is_admin === true)) {
          setDoc(doc(db, 'users', firebaseUser.uid), {
            role: profileData.education_level ? 'student' : 'general',
            is_admin: false,
          }, { merge: true }).catch(() => {});
        }
        deleteDoc(doc(db, 'admins', firebaseUser.uid)).catch(() => {});
      }
    } catch (err) {
      console.warn('Could not load user profile from Firestore (offline/quota):', err?.message || err);
    }
  }

  try {
    const cached = JSON.parse(localStorage.getItem(`dp_profile_${firebaseUser.uid}`) || '{}');
    profileData = { ...profileData, ...cached };

    // Check central registered list
    const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
    const userEmail = (firebaseUser.email || '').toLowerCase().trim();
    const regMatch = regList.find(
      (u) =>
        u.id === firebaseUser.uid ||
        (u.email && userEmail && u.email.toLowerCase().trim() === userEmail)
    );
    if (regMatch) {
      profileData = { ...profileData, ...regMatch };
    }

    // Check admin overrides (check by uid, email, and regMatch.id)
    const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
    const override =
      overrides[firebaseUser.uid] ||
      (userEmail && overrides[userEmail]) ||
      (regMatch?.id && overrides[regMatch.id]);
    if (override) {
      profileData = { ...profileData, ...override };
    }
  } catch {}

  const userObj = basicUser(firebaseUser, profileData);
  try {
    localStorage.setItem(`dp_profile_${firebaseUser.uid}`, JSON.stringify(profileData));
  } catch {}

  return userObj;
};

const saveProfile = async (firebaseUser, profile = {}) => {
  if (!firebaseUser) return;
  const isActualAdmin = isEmailAdmin(firebaseUser.email);
  const isStudent = profile.role === 'student' || profile.verification_requested_role === 'student';
  const isDoctor = profile.role === 'doctor' || profile.verification_requested_role === 'doctor';

  const isApproved = isActualAdmin
    ? true
    : Boolean(profile.is_approved === true || profile.verification_status === 'approved');

  const isRejected = !isActualAdmin && profile.verification_status === 'rejected';

  const verificationStatus = isActualAdmin
    ? 'approved'
    : (isApproved ? 'approved' : (isRejected ? 'rejected' : 'pending'));

  const safeProfile = {
    id: firebaseUser.uid,
    full_name: profile.full_name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || (isDoctor ? 'Doctor' : isStudent ? 'Student' : 'User'),
    email: (firebaseUser.email || profile.email || '').toLowerCase().trim(),
    role: isActualAdmin ? 'admin' : (profile.role === 'admin' ? 'general' : (profile.role || 'general')),
    country: profile.country || '',
    education_level: profile.education_level || '',
    college_name: profile.college_name || profile.campus_name || '',
    campus_name: profile.campus_name || profile.college_name || '',
    goal: profile.goal || '',
    is_admin: isActualAdmin,
    is_approved: isApproved,
    verification_status: verificationStatus,
    verification_requested_role: profile.verification_requested_role || profile.role || '',
    verification_submitted_at: profile.verification_submitted_at || new Date().toISOString(),
    verification_notes: profile.verification_notes || '',
    student_id_front: profile.student_id_front || '',
    student_id_back: profile.student_id_back || '',
    specialization: profile.specialization || '',
    qualifications: profile.qualifications || '',
    license_number: profile.license_number || '',
    hospital_affiliation: profile.hospital_affiliation || '',
    consultation_fee: profile.consultation_fee || '₹1,000',
    experience_years: profile.experience_years || '',
    phone: profile.phone || '',
    city: profile.city || '',
    bio: profile.bio || '',
    updated_at: new Date().toISOString(),
  };

  // 1. Cache individual profile for current user
  try {
    localStorage.setItem(`dp_profile_${firebaseUser.uid}`, JSON.stringify(safeProfile));
  } catch (err) {
    console.warn('localStorage cache notice:', err);
  }

  // 2. Add to central persistent registered users registry
  try {
    const list = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
    const existingIndex = list.findIndex(
      (u) => u.id === safeProfile.id || (u.email && u.email.toLowerCase() === safeProfile.email.toLowerCase())
    );
    if (existingIndex >= 0) {
      list[existingIndex] = { ...list[existingIndex], ...safeProfile };
    } else {
      list.unshift(safeProfile);
    }
    localStorage.setItem('drpulse_registered_users', JSON.stringify(list));
  } catch (err) {
    console.warn('Failed to save to central registered users list:', err);
  }

  // 3. Save to Firestore (non-blocking)
  if (db) {
    try {
      await setDoc(
        doc(db, 'users', firebaseUser.uid),
        { ...safeProfile, created_at: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.warn('Firestore setDoc notice (will rely on local registry):', err?.message || err);
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('dp_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      localStorage.removeItem('dp_token');
      setToken(null);
      setLoading(false);
      return undefined;
    }

    // Safety timeout: Ensure loading is NEVER stuck permanently even if network/Firestore stalls
    const safetyTimer = setTimeout(() => {
      setLoading(false);
    }, 2500);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(safetyTimer);
      try {
        if (!firebaseUser) {
          localStorage.removeItem('dp_token');
          setToken(null);
          setUser(null);
          return;
        }

        const idToken = await firebaseUser.getIdToken().catch(() => localStorage.getItem('dp_token') || '');
        if (idToken) {
          localStorage.setItem('dp_token', idToken);
          setToken(idToken);
        }
        const appUser = await loadProfile(firebaseUser).catch(() => basicUser(firebaseUser));
        setUser(appUser);
      } catch (e) {
        console.warn('Auth state sync notice:', e?.message || e);
        if (firebaseUser) {
          setUser(basicUser(firebaseUser));
        } else {
          localStorage.removeItem('dp_token');
          setToken(null);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(safetyTimer);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const login = useCallback(async (email, password) => {
    ensureFirebase();
    const result = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await result.user.getIdToken();
    const appUser = await loadProfile(result.user);
    localStorage.setItem('dp_token', idToken);
    setToken(idToken);
    setUser(appUser);
    return appUser;
  }, []);

  const signup = useCallback(async (payload) => {
    ensureFirebase();
    const cleanEmail = (payload.email || '').toLowerCase().trim();
    let resultUser = null;
    let isReapplication = false;

    try {
      const res = await createUserWithEmailAndPassword(auth, cleanEmail, payload.password);
      resultUser = res.user;
    } catch (createErr) {
      const isEmailInUse =
        createErr?.code === 'auth/email-already-in-use' ||
        createErr?.message?.includes('auth/email-already-in-use');

      if (isEmailInUse) {
        // Check if this existing email belongs to a rejected applicant
        let wasRejected = false;
        let existingId = null;

        // 1. Check local registered users list
        try {
          const regList = JSON.parse(localStorage.getItem('drpulse_registered_users') || '[]');
          const match = regList.find((u) => (u.email || '').toLowerCase().trim() === cleanEmail);
          if (match) {
            existingId = match.id;
            const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
            const ov = overrides[match.id] || {};
            const status = ov.verification_status || match.verification_status;
            if (status === 'rejected') {
              wasRejected = true;
            }
          }
        } catch {}

        // 2. Check dp_profile_*
        if (!wasRejected) {
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const k = localStorage.key(i);
              if (k && k.startsWith('dp_profile_')) {
                const raw = localStorage.getItem(k);
                if (raw) {
                  const p = JSON.parse(raw);
                  if ((p.email || '').toLowerCase().trim() === cleanEmail) {
                    existingId = k.replace('dp_profile_', '');
                    const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
                    const ov = overrides[existingId] || {};
                    const status = ov.verification_status || p.verification_status;
                    if (status === 'rejected') {
                      wasRejected = true;
                    }
                    break;
                  }
                }
              }
            }
          } catch {}
        }

        // 3. Try signing in with the password provided during re-registration
        try {
          const signInRes = await signInWithEmailAndPassword(auth, cleanEmail, payload.password);
          resultUser = signInRes.user;

          // If not detected locally, check Firestore
          if (!wasRejected && db) {
            try {
              const snap = await getDoc(doc(db, 'users', resultUser.uid));
              if (snap.exists() && snap.data()?.verification_status === 'rejected') {
                wasRejected = true;
              }
            } catch {}
          }

          if (wasRejected) {
            isReapplication = true;
            // Clear old rejection override
            try {
              const overrides = JSON.parse(localStorage.getItem('drpulse_admin_users_overrides') || '{}');
              delete overrides[resultUser.uid];
              if (existingId) delete overrides[existingId];
              localStorage.setItem('drpulse_admin_users_overrides', JSON.stringify(overrides));
            } catch {}
          } else {
            // Email is already registered and NOT rejected (active or approved)
            throw createErr;
          }
        } catch (authErr) {
          if (authErr === createErr || authErr?.code === 'auth/email-already-in-use') {
            throw createErr;
          }
          if (wasRejected && (authErr?.code === 'auth/wrong-password' || authErr?.code === 'auth/invalid-credential')) {
            throw new Error('Your previous verification was declined. To re-register with this email, please enter your existing account password so we can verify and update your credentials.');
          }
          throw createErr;
        }
      } else {
        throw createErr;
      }
    }

    await updateProfile(resultUser, { displayName: payload.full_name }).catch(() => {});

    const isPatient = payload.role === 'general' || payload.role === 'patient';
    const cleanPayload = {
      ...payload,
      role: isPatient ? 'general' : payload.role,
      id: resultUser.uid,
      is_approved: isPatient ? true : false,
      verification_status: isPatient ? 'approved' : 'pending',
      verification_requested_role: isPatient ? 'general' : payload.role,
      verification_submitted_at: new Date().toISOString(),
      verification_notes: '',
    };

    await saveProfile(resultUser, cleanPayload);
    const idToken = await resultUser.getIdToken();
    const appUser = await loadProfile(resultUser);
    localStorage.setItem('dp_token', idToken);
    setToken(idToken);
    setUser(appUser);
    return appUser;
  }, []);

  const googleLogin = useCallback(async () => {
    ensureFirebase();
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await saveProfile(result.user);
      const idToken = await result.user.getIdToken();
      const appUser = await loadProfile(result.user);
      localStorage.setItem('dp_token', idToken);
      setToken(idToken);
      setUser(appUser);
      return appUser;
    } catch (error) {
      throw new Error(authErrorMessage(error, 'Google login failed'));
    }
  }, []);

  const updateUserProfile = useCallback(async (updates) => {
    const uid = auth?.currentUser?.uid || user?.id;
    if (!uid) return;
    const isActualAdmin = isEmailAdmin(auth?.currentUser?.email || user?.email);
    const sanitizedUpdates = { ...updates };
    if (!isActualAdmin) {
      delete sanitizedUpdates.is_admin;
      if (sanitizedUpdates.role === 'admin') delete sanitizedUpdates.role;
    }
    if (db && auth?.currentUser) {
      try {
        await setDoc(doc(db, 'users', uid), { ...sanitizedUpdates, updated_at: serverTimestamp() }, { merge: true });
      } catch (err) {
        console.warn('Firestore user profile sync warning:', err);
      }
    }
    try {
      const cached = JSON.parse(localStorage.getItem(`dp_profile_${uid}`) || '{}');
      localStorage.setItem(`dp_profile_${uid}`, JSON.stringify({ ...cached, ...sanitizedUpdates }));
    } catch {}
    setUser((prev) => (prev ? { ...prev, ...sanitizedUpdates } : null));
  }, [user]);

  const demoLogin = useCallback(async () => {
    throw new Error('Hardcoded demo accounts have been removed.');
  }, []);

  const logout = useCallback(async () => {
    if (auth) await signOut(auth);
    localStorage.removeItem('dp_token');
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, googleLogin, logout, updateUserProfile, demoLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
